/**
 * GSCConnectionModal - Modal for linking Google Search Console
 *
 * Flow:
 * 1. User clicks "Link Search Console"
 * 2. OAuth popup opens for Google consent
 * 3. After consent, user sees list of their GSC properties
 * 4. User selects a property to link
 * 5. Data import starts in background
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { gscApi } from '@/services/api/gsc';
import { termsApi } from '@/services/api/terms';
import { debug } from '@/utils/debug';
import { formatSiteUrl } from '@/utils/urlUtils';

interface GSCConnectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConnected?: (siteUrl: string) => void;
  /** When true, skip success screen and go directly to property selection */
  isChangingProperty?: boolean;
}

type Step = 'start' | 'oauth' | 'select-property' | 'success';

export function GSCConnectionModal({ isOpen, onClose, onConnected, isChangingProperty }: GSCConnectionModalProps) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>('start');
  const [selectedProperty, setSelectedProperty] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [oauthPopup, setOauthPopup] = useState<Window | null>(null);
  const [pollInterval, setPollInterval] = useState<ReturnType<typeof setInterval> | null>(null);

  // Check connection status on mount
  const { data: connectionStatus, refetch: refetchStatus } = useQuery({
    queryKey: ['gsc-connection-status'],
    queryFn: gscApi.getConnectionStatus,
    enabled: isOpen,
  });

  // Fetch properties after OAuth is complete
  const { data: properties, isLoading: propertiesLoading, refetch: refetchProperties } = useQuery({
    queryKey: ['gsc-properties'],
    queryFn: gscApi.listProperties,
    enabled: isOpen && connectionStatus?.oauth_completed === true,
  });

  // Cleanup function for OAuth popup
  const cleanupOAuth = () => {
    if (pollInterval) {
      clearInterval(pollInterval);
      setPollInterval(null);
    }
    if (oauthPopup && !oauthPopup.closed) {
      oauthPopup.close();
    }
    setOauthPopup(null);
  };

  // Start OAuth mutation
  const startOAuthMutation = useMutation({
    mutationFn: gscApi.startOAuth,
    onSuccess: (data) => {
      // Clean up any existing OAuth attempt first
      cleanupOAuth();

      // Open OAuth popup with unique name to prevent caching issues
      const popupName = `gsc-oauth-${Date.now()}`;
      const popup = window.open(
        data.authorization_url,
        popupName,
        'width=600,height=700,menubar=no,toolbar=no'
      );
      setOauthPopup(popup);

      // Poll for popup close
      const interval = setInterval(() => {
        if (popup?.closed) {
          clearInterval(interval);
          setPollInterval(null);
          setOauthPopup(null);
          // Refetch connection status
          refetchStatus().then(() => {
            refetchProperties();
            setStep('select-property');
          });
        }
      }, 500);
      setPollInterval(interval);

      setStep('oauth');
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  // Select property mutation
  const selectPropertyMutation = useMutation({
    mutationFn: (siteUrl: string) => gscApi.selectProperty(siteUrl),
    onSuccess: (_, siteUrl) => {
      queryClient.invalidateQueries({ queryKey: ['gsc-connection-status'] });
      queryClient.invalidateQueries({ queryKey: ['gsc-settings'] });
      setStep('success');
      onConnected?.(siteUrl);
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [pollInterval]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setError(null);
      setSelectedProperty(null);
      setTermsAccepted(false);
      setIsSubmitting(false);

      // When changing property, go directly to property selection
      if (isChangingProperty && connectionStatus?.oauth_completed) {
        setStep('select-property');
      } else if (connectionStatus?.oauth_completed && !connectionStatus?.site_url) {
        setStep('select-property');
      } else if (connectionStatus?.site_url && !isChangingProperty) {
        setStep('success');
      } else {
        setStep('start');
      }
    } else {
      // Cleanup when modal closes
      if (pollInterval) {
        clearInterval(pollInterval);
        setPollInterval(null);
      }
    }
  }, [isOpen, connectionStatus, isChangingProperty, pollInterval]);

  // Cleanup OAuth when closing
  const handleClose = () => {
    cleanupOAuth();
    onClose();
  };

  if (!isOpen) return null;

  const handleStartOAuth = async () => {
    if (!termsAccepted || isSubmitting) return;

    setError(null);
    setIsSubmitting(true);

    // Record terms acceptance
    try {
      await termsApi.acceptTermsForAccountConnection();
      debug.log('Terms acceptance recorded for GSC connection');
    } catch (error) {
      // Log but don't block - terms were accepted via checkbox
      debug.error('Failed to record terms acceptance for GSC connection:', error);
    }

    setIsSubmitting(false);
    startOAuthMutation.mutate();
  };

  const handleSelectProperty = () => {
    if (selectedProperty) {
      selectPropertyMutation.mutate(selectedProperty);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold">Link Search Console</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Step 1: Start */}
          {step === 'start' && (
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium mb-2">Link Google Search Console</h3>
              <p className="text-gray-600 mb-4">
                Link your Search Console to see organic search data alongside your Google Ads performance.
                This helps identify which keywords have strong organic presence.
              </p>

              {/* Terms Checkbox */}
              <div className="mb-6 text-left">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">
                    I agree to the{' '}
                    <Link
                      to="/terms"
                      target="_blank"
                      className="text-blue-600 hover:text-blue-800 underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Terms & Conditions
                    </Link>{' '}
                    and{' '}
                    <Link
                      to="/privacy"
                      target="_blank"
                      className="text-blue-600 hover:text-blue-800 underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Privacy Policy
                    </Link>
                  </span>
                </label>
              </div>

              <Button
                onClick={handleStartOAuth}
                disabled={!termsAccepted || isSubmitting || startOAuthMutation.isPending}
              >
                {isSubmitting || startOAuthMutation.isPending ? 'Linking...' : 'Link with Google'}
              </Button>
            </div>
          )}

          {/* Step 2: OAuth in progress */}
          {step === 'oauth' && (
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <LoadingSpinner size="xl" variant="primary" label="Signing in with Google" />
              </div>
              <h3 className="text-lg font-medium mb-2">Complete Sign-In</h3>
              <p className="text-gray-600">
                A popup window has opened. Please sign in with Google and grant access to Search Console.
              </p>
            </div>
          )}

          {/* Step 3: Select Property */}
          {step === 'select-property' && (
            <div>
              <h3 className="text-lg font-medium mb-2">Select a Property</h3>
              <p className="text-gray-600 mb-4">
                Choose which Search Console property to link.
              </p>

              {propertiesLoading ? (
                <div className="text-center py-8 text-gray-500">
                  Loading properties...
                </div>
              ) : properties && properties.length > 0 ? (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {properties.map((property) => {
                    // Make site_url human readable
                    const displayName = formatSiteUrl(property.site_url);
                    const propertyType = property.site_url.startsWith('sc-domain:') ? 'Domain Property' : 'URL Prefix';

                    return (
                      <label
                        key={property.site_url}
                        className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                          selectedProperty === property.site_url
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="property"
                          value={property.site_url}
                          checked={selectedProperty === property.site_url}
                          onChange={() => setSelectedProperty(property.site_url)}
                          className="mr-3"
                        />
                        <div className="flex-1">
                          <div className="font-medium">{displayName}</div>
                          <div className="text-sm text-gray-500">
                            {propertyType} · {property.permission_level}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No Search Console properties found. Make sure you have at least one property in your Search Console.
                </div>
              )}

              <div className="mt-6 flex justify-end gap-3">
                <Button variant="secondary" onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSelectProperty}
                  disabled={!selectedProperty || selectPropertyMutation.isPending}
                >
                  {selectPropertyMutation.isPending ? 'Linking...' : 'Link Property'}
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Success */}
          {step === 'success' && (
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-medium mb-2">Search Console Linked!</h3>
              <p className="text-gray-600 mb-2">
                {connectionStatus?.site_url || selectedProperty}
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Organic data is being imported in the background. This may take a few minutes.
              </p>
              <Button onClick={handleClose}>
                Done
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default GSCConnectionModal;
